import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { useMe } from "@/hooks/use-auth";
import { useCreateProduct, useUpdateProduct } from "@/hooks/use-products";
import { compressImage } from "@/lib/compress-image";
import { getErrorMessage } from "@/lib/error-message";
import { storageService } from "@/services/storage";

import {
  defaultProductValues,
  productSchema,
  type ProductFormValues,
} from "./product-schema";
import { useProductRecipeForm } from "./use-product-recipe-form";

import type { Product } from "@artisancode/api-types";

export function useProductForm(
  open: boolean,
  editing: Product | null,
  onSuccess: () => void,
) {
  const { mutateAsync: create, isPending: isCreating } = useCreateProduct();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateProduct();
  const { data: me } = useMe();

  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: defaultProductValues,
  });

  const recipe = useProductRecipeForm({
    product: editing,
    open,
    sellingPrice: useWatch({ control: form.control, name: "sellingPrice" }),
  });
  const isProduced =
    useWatch({ control: form.control, name: "sourcingType" }) === "produced";

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedPhoto(null);
      setRemovePhoto(false);
      setPhotoPreview(null);

      form.reset(
        editing
          ? {
              name: editing.name,
              sku: editing.sku ?? "",
              category: editing.category,
              menuGroup: editing.menuGroup,
              sourcingType: editing.sourcingType ?? "produced",
              photoKey: editing.photoKey ?? null,
              sellingPrice: editing.sellingPrice ?? undefined,
              minStock: editing.minStock ?? undefined,
              notes: editing.notes ?? "",
            }
          : { ...defaultProductValues, workspaceId: me?.workspaceId ?? "" },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, me?.workspaceId]);

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  function onSelectPhoto(file: File) {
    setSelectedPhoto(file);
    setRemovePhoto(false);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  }

  function onRemovePhoto() {
    setSelectedPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    setRemovePhoto(true);
  }

  const photoDisplayUrl =
    photoPreview ?? (!removePhoto ? (editing?.photoUrl ?? null) : null);

  async function onSubmit(values: ProductFormValues) {
    const needsRecipe = values.sourcingType === "produced";
    if (needsRecipe && !recipe.hasIngredients) {
      toast.error(
        "Produk racikan wajib punya resep: tambahkan minimal 1 bahan baku.",
      );
      return;
    }

    try {
      let photoKey: string | null | undefined = undefined;

      if (selectedPhoto) {
        setIsUploading(true);
        const compressed = await compressImage(selectedPhoto);
        photoKey = await storageService.uploadFile(compressed, "products");
      } else if (removePhoto) {
        photoKey = null;
      }

      let productId: string;
      if (editing) {
        // Note: sku is intentionally immutable on edit - never updated!
        await update({
          id: editing.id,
          name: values.name,
          category: values.category,
          menuGroup: values.menuGroup,
          sourcingType: values.sourcingType,
          sellingPrice: values.sellingPrice,
          minStock: values.minStock ?? null,
          notes: values.notes,
          ...(photoKey !== undefined ? { photoKey } : {}),
        });
        productId = editing.id;
      } else {
        const created = await create({
          ...values,
          sku: values.sku?.trim() || undefined,
          minStock: values.minStock ?? null,
          workspaceId: values.workspaceId || undefined,
          ...(photoKey !== undefined ? { photoKey } : {}),
        });
        productId = created.id;
      }

      if (needsRecipe && !(await recipe.saveRecipe(productId))) {
        toast.error(
          "Produk tersimpan, tapi resep gagal. Buka produk lalu simpan ulang resepnya.",
        );
      } else {
        toast.success(
          editing
            ? "Produk berhasil diperbarui."
            : "Produk berhasil ditambahkan.",
        );
      }
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, "Gagal menyimpan produk."));
    } finally {
      setIsUploading(false);
    }
  }

  return {
    form,
    recipe,
    isProduced,
    isSubmitting: isCreating || isUpdating || isUploading,
    isUploading,
    photoDisplayUrl,
    onSelectPhoto,
    onRemovePhoto,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
